import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  Wrap,
  Header,
  Title,
  Subtitle,
  Description,
  Viewport,
  Track,
  Card,
  ImgWrap,
  Img,
  Caption,
  ArrowLeft,
  ArrowRight,
} from "./ContinuousCarouselStyles";

export default function ContinuousCarousel({
  title,
  subtitle,
  description,
  items = [],
  speedSec = 55,
  gap = 46,
  cardW = 380,
  cardH = 420,
  pauseOnHover = true,
}) {
  const loopItems = useMemo(() => [...items, ...items], [items]);

  const [paused, setPaused] = useState(false);
  const [offset, setOffset] = useState(0);

  const rafRef = useRef(null);
  const lastRef = useRef(0);

  const step = cardW + gap;

  const loopWidth = Math.max(1, items.length) * step;

  const pxPerSec = loopWidth / Math.max(1, speedSec);

  const normalize = (x) => {
    if (!isFinite(x)) return 0;
    let v = x % loopWidth;
    if (v > 0) v -= loopWidth;
    return v;
  };

  useEffect(() => {
    if (!items.length) return;

    const tick = (t) => {
      if (!lastRef.current) lastRef.current = t;
      const dt = (t - lastRef.current) / 1000;
      lastRef.current = t;

      if (!paused) {
        setOffset((prev) => normalize(prev - pxPerSec * dt));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastRef.current = 0;
    };
  }, [items.length, paused, pxPerSec, loopWidth]);

  const handleNext = () => {
    setOffset((prev) => normalize(prev - step));
  };

  const handlePrev = () => {
    setOffset((prev) => normalize(prev + step));
  };

  const onEnter = () => {
    if (pauseOnHover) setPaused(true);
  };

  const onLeave = () => {
    if (pauseOnHover) setPaused(false);
  };

  return (
    <Wrap>
      <Header>
        {title && <Title>{title}</Title>}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Header>

      <Viewport onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <ArrowLeft onClick={handlePrev} aria-label="Prev">
          ‹
        </ArrowLeft>
        <ArrowRight onClick={handleNext} aria-label="Next">
          ›
        </ArrowRight>

        <Track
          $gap={gap}
          style={{
            "--cardW": `${cardW}px`,
            "--cardH": `${cardH}px`,
            transform: `translate3d(${offset}px, 0, 0)`,
          }}
        >
          {loopItems.map((it, idx) => (
            <Card key={`${it.id ?? it.title ?? "item"}-${idx}`}>
              <ImgWrap>
                <Img src={it.image} alt={it.title} loading="lazy" />
              </ImgWrap>
              <Caption>{it.title}</Caption>
            </Card>
          ))}
        </Track>
      </Viewport>

      <Header>{description && <Description>{description}</Description>}</Header>
    </Wrap>
  );
}
